"use client";

import { useState } from "react";
import { Bike, Check, Copy, Facebook, Instagram, Mail, MapPin, CircleParking as ParkingCircle, Phone, TrainFront as Train, Video } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Serviceareas() {
  const [copied, setCopied] = useState(false);
  const address = "Merrydown, Kingston Lane, East Preston, BN16 1RP";
  const phone = "01903 372801";
  const email = "info@coastline-services.co.uk";
  const hours = [{
    day: "Monday",
    time: "8.30 to 4.30pm",
    open: true
  }, {
    day: "Tuesday",
    time: "8.30 to 4.30pm",
    open: true
  }, {
    day: "Wednesday",
    time: "8.30 to 4.30pm",
    open: true
  }, {
    day: "Thursday",
    time: "8.30 to 4.30pm",
    open: true
  }, {
    day: "Friday",
    time: "8.30 to 4.30pm",
    open: true
  }, {
    day: "Saturday",
    time: "Closed",
    open: false
  }, {
    day: "Sunday",
    time: "Closed",
    open: false
  }];
  const jsDay = new Date().getDay();
  const currentDayIndex = jsDay === 0 ? 6 : jsDay - 1;
  const infoChips = [{
    icon: ParkingCircle,
    label: "Customer parking available"
  }, {
    icon: Train,
    label: "Near Crawley Station"
  }, {
    icon: Bike,
    label: "Secure bike storage"
  }];
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(address);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="159" id="serviceareas" className="bg-background pt-[100px] pb-20 md:pb-28">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div ref={leftRef} className={`lg:col-span-5 transition-all duration-700 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <p className="font-default text-xs uppercase tracking-[0.2em] text-muted-foreground mb-5">
              Visit Our Office
            </p>
            <h1 className="font-default text-foreground text-4xl md:text-[44px] leading-[1.1] tracking-tight font-bold">
              Our Service Hub —
              <br />
              <span className="text-primary">ready to assist you.</span>
            </h1>

            {}
            <div className="mt-10">
              <div className="flex items-start gap-3">
                <div className="mt-1 flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground shrink-0">
                  <MapPin className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-default text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-1.5">
                    Headquarters
                  </p>
                  <div className="flex items-start gap-2">
                    <span className="font-default text-foreground text-[22px] leading-snug">
                      {address}
                    </span>
                    <Button variant="ghost" size="icon" onClick={handleCopy} className="mt-1.5 h-6 w-6 text-muted-foreground hover:text-foreground hover:bg-muted shrink-0" aria-label="Copy address">
                      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                    </Button>
                  </div>
                  
                </div>
              </div>
            </div>

            <Separator className="my-8 bg-border" />

            {}
            <div>
              <p className="font-default text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-4">
                Opening Hours
              </p>
              <ul className="space-y-1.5">
                {hours.map((h, i) => {
                const isCurrent = i === currentDayIndex;
                return <li data-index={i} key={h.day} className={`flex items-center justify-between rounded-lg px-3 py-2 transition-colors ${isCurrent ? "bg-primary/10" : ""}`}>
                      <div className="flex items-center gap-3">
                        <span className={`text-sm font-medium ${isCurrent ? "text-primary" : "text-foreground"}`}>
                          {h.day}
                        </span>
                        {isCurrent && <span className="font-default text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-md bg-primary text-primary-foreground">
                            Open now
                          </span>}
                      </div>
                      <span className={`font-default text-base ${isCurrent ? "text-primary font-semibold" : "text-muted-foreground"}`}>
                        {h.time}
                      </span>
                    </li>;
              })}
              </ul>
            </div>

            <Separator className="my-8 bg-border" />

            {}
            <div>
              <p className="font-default text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-4">
                Contact
              </p>
              <ul className="space-y-3">
                <li className="flex items-center gap-3">
                  <Phone className="h-4 w-4 text-primary shrink-0" />
                  <Link to={`tel:${phone}`} className="font-default text-foreground text-lg hover:text-primary transition-colors">
                    {phone}
                  </Link>
                </li>
                <li className="flex items-center gap-3">
                  <Mail className="h-4 w-4 text-primary shrink-0" />
                  <Link to={`mailto:${email}`} className="font-default text-foreground text-lg hover:text-primary transition-colors break-all">
                    {email}
                  </Link>
                </li>
              </ul>
              
            </div>

            {}
            <div className="mt-6 flex items-center gap-2">
              <Link to="#" className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border text-foreground hover:bg-primary hover:text-primary-foreground transition-colors" aria-label="Instagram">
                <Instagram className="h-4 w-4" />
              </Link>
              <Link to="#" className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border text-foreground hover:bg-primary hover:text-primary-foreground transition-colors" aria-label="Facebook">
                <Facebook className="h-4 w-4" />
              </Link>
            </div>

            {}
            <Link to="/MaintenancePage" className="mt-8 group flex items-center gap-4 rounded-lg bg-primary p-5 hover:bg-primary/90 transition-colors text-primary-foreground">
              
              <div className="flex-1 min-w-0">
                
                
              </div>
            </Link>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-7 transition-all duration-700 ease-out delay-100 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="relative overflow-hidden rounded-lg border border-border shadow-lg bg-card h-auto md:h-[600px]">
              {}
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717132/img/coastline_service_map.png" alt="Service area map for Coastline covering Sussex and Surrey" loading="lazy" decoding="async" className="absolute inset-0 h-full w-full max-w-full object-cover" />

              {}
              <div className="absolute inset-0 bg-primary/5 pointer-events-none" />

              {}
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
                <div className="relative flex flex-col items-center">
                  {}
                  <div className="mb-3 rounded-lg bg-background px-4 py-3 shadow-lg border border-border min-w-[200px]">
                    <p className="font-default text-sm text-primary font-semibold mb-0.5">
                      Coastline HQ
                    </p>
                    <p className="font-default text-foreground text-base font-bold leading-tight">
                      Climate Control Experts
                    </p>
                    <p className="font-default text-[10px] uppercase tracking-wider text-muted-foreground mt-1">
                      Open now · 8am – 6pm
                    </p>
                    {}
                    <div className="absolute left-1/2 -translate-x-1/2 -bottom-1.5 h-3 w-3 rotate-45 bg-background border-r border-b border-border" />
                  </div>

                  {}
                  <div className="relative">
                    <div className="absolute inset-0 rounded-lg bg-primary/30 blur-xl scale-150" />
                    <div className="relative flex h-12 w-12 items-center justify-center rounded-lg bg-primary text-primary-foreground shadow-lg ring-4 ring-background">
                      <MapPin className="h-6 w-6" />
                    </div>
                    {}
                    <div className="mx-auto mt-1 h-1.5 w-6 rounded-lg bg-foreground/20 blur-sm" />
                  </div>
                </div>
              </div>
            </div>

            {}
            <div className="mt-6 flex flex-wrap gap-2.5">
              {infoChips.map((chip, i) => {
              const Icon = chip.icon;
              return <div data-index={i} key={chip.label} className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3.5 py-2 text-sm text-foreground">
                    
                    
                  </div>;
            })}
            </div>

            {}
            <div className="mt-6">
              <Button asChild size="lg" className="rounded-lg bg-primary text-primary-foreground">
                <Link to="https://www.google.com/maps/search/?api=1&query=Merrydown+Kingston+Lane+East+Preston+BN16+1RP" newTab>
                  Open in Google Maps
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}